import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../layout/default";
import { Block, Icon } from "../components";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useApiClient } from "../ApiService/useApiClient";

const safeArr = (v) => (Array.isArray(v) ? v : []);

const startOfMonthISO = () => {
  const d = new Date();
  const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0));
  return start.toISOString().slice(0, 10);
};

const endOfMonthISO = () => {
  const d = new Date();
  const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 23, 59, 59));
  return end.toISOString().slice(0, 10);
};

const toISODateTimeRange = (dateStr, endOfDay = false) => {
  if (!dateStr) return null;
  return endOfDay ? `${dateStr}T23:59:59Z` : `${dateStr}T00:00:00Z`;
};

const ymd = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toISOString().slice(0, 10);
};

const pill = (bg, text) => (
  <Badge bg={bg} className="px-2 py-1">
    {text}
  </Badge>
);

const typeBadge = (type) => {
  const t = String(type || "").toLowerCase();
  if (t.includes("holiday")) return pill("primary", "Holiday");
  if (t.includes("off") || t.includes("sick")) return pill("danger", "Off sick");
  return pill("secondary", type || "Absence");
};

const statusBadge = (status) => {
  const s = normStatus(status);

  if (s === "approved") return pill("success", "Approved");
  if (s === "reported") return pill("warning", "Reported");
  if (s === "scheduled") return pill("info", "Scheduled");
  if (s === "cancelled") return pill("secondary", "Cancelled");

  return pill("dark", status || "—");
};


const normStatus = (status) => String(status || "").toLowerCase().trim();
const isApproved = (status) => normStatus(status) === "approved";
const isCancelled = (status) => normStatus(status) === "cancelled";

// ✅ "Reported" or "Scheduled" are pending-like (approvable)
const isPending = (status) => {
  const s = normStatus(status);
  return s === "reported" || s === "scheduled";
};


export default function StaffAbsencesMonth() {
  const api = useApiClient();

  // Filters
  const [fromDate, setFromDate] = useState(startOfMonthISO());
  const [toDate, setToDate] = useState(endOfMonthISO());
  const [type, setType] = useState("all"); // all | holiday | offsick
  const [status, setStatus] = useState("all"); // all | pending | approved | cancelled
  const [agentId, setAgentId] = useState(""); // numeric or ""
  const [search, setSearch] = useState("");

  // Data
  const [items, setItems] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]); // optional
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Action state
  const [actionBusyId, setActionBusyId] = useState(null);

  // Cancel confirm modal
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  // OPTIONAL: load staff list (if you have a summary endpoint)
  const loadStaffList = useCallback(async () => {
    try {
      // Uncomment if available:
      // const res = await api.get("HMCareStaff/stafflistsummary/");
      // setStaffOptions(safeArr(res.data));
    } catch {
      // ignore
    }
  }, [api]);

  const fetchAbsences = useCallback(async () => {
    setLoading(true);
    setErr(null);

    try {
      const from = toISODateTimeRange(fromDate, false);
      const to = toISODateTimeRange(toDate, true);

      const params = {
        from,
        to,
        type,
        status,
        agentId: agentId ? Number(agentId) : undefined,
        search: search?.trim() ? search.trim() : undefined,
      };

      // ✅ same endpoint requested
      const res = await api.get("StaffAbsences/dash", params);

      const data = res.data;
      const list = Array.isArray(data) ? data : safeArr(data?.items);

      setItems(list);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, [api, fromDate, toDate, type, status, agentId, search]);

  useEffect(() => {
    loadStaffList();
    fetchAbsences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApply = (e) => {
    e.preventDefault();
    fetchAbsences();
  };

  const onReset = () => {
    setFromDate(startOfMonthISO());
    setToDate(endOfMonthISO());
    setType("all");
    setStatus("all");
    setAgentId("");
    setSearch("");
    setTimeout(fetchAbsences, 0);
  };

  const totals = useMemo(() => {
    const total = items.length;
    const holidays = items.filter((x) => String(x.type || x.absenceType || "").toLowerCase().includes("holiday")).length;
    const sick = items.filter((x) => {
      const t = String(x.type || x.absenceType || "").toLowerCase();
      return t.includes("off") || t.includes("sick");
    }).length;
    return { total, holidays, sick };
  }, [items]);

  // ---- Actions ----
  const approveAbsence = async (id) => {
    if (!id) return;
    setActionBusyId(id);
    setErr(null);
    try {
      await api.post(`StaffAbsences/${id}/approve`, {});
      await fetchAbsences();
    } catch (e) {
      setErr(e);
    } finally {
      setActionBusyId(null);
    }
  };

  const openCancelConfirm = (absence) => {
    setCancelTarget(absence);
    setConfirmCancelOpen(true);
  };

  const closeCancelConfirm = () => {
    setConfirmCancelOpen(false);
    setCancelTarget(null);
  };

  const cancelAbsence = async () => {
    const id = cancelTarget?.id ?? cancelTarget?.absenceId ?? cancelTarget?.staffAbsenceId;
    if (!id) return;

    setActionBusyId(id);
    setErr(null);

    try {
      await api.post(`StaffAbsences/${id}/cancel`, {});
      closeCancelConfirm();
      await fetchAbsences();
    } catch (e) {
      setErr(e);
    } finally {
      setActionBusyId(null);
    }
  };

  return (
    <Layout title="Staff Absences" content="container">
      <Block>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <div className="h4 mb-1">Staff Holidays & Off-sick</div>
            <div className="small text-muted">
              Showing absences for <span className="fw-semibold">{fromDate}</span> to{" "}
              <span className="fw-semibold">{toDate}</span>
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={fetchAbsences} disabled={loading}>
              <Icon name="refresh" /> <span>Refresh</span>
            </Button>
          </div>
        </div>

        {err ? (
          <Alert variant="danger">
            {String(err?.response?.data?.message || err?.message || err)}
          </Alert>
        ) : null}

        {/* Filters */}
        <Card className="mb-3">
          <Card.Body>
            <Form onSubmit={onApply}>
              <Row className="g-3 align-items-end">
                <Col xs={12} md={3}>
                  <Form.Label>From</Form.Label>
                  <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </Col>

                <Col xs={12} md={3}>
                  <Form.Label>To</Form.Label>
                  <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </Col>

                <Col xs={12} md={2}>
                  <Form.Label>Type</Form.Label>
                  <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="all">All</option>
                    <option value="holiday">Holiday</option>
                    <option value="offsick">Off sick</option>
                  </Form.Select>
                </Col>

                <Col xs={12} md={2}>
                  <Form.Label>Status</Form.Label>
                  <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Col>

                <Col xs={12} md={2}>
                  <Form.Label>Staff</Form.Label>
                  {staffOptions.length ? (
                    <Form.Select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                      <option value="">All staff</option>
                      {staffOptions.map((s) => (
                        <option key={s.agentId ?? s.id} value={s.agentId ?? s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="number"
                      placeholder="Agent ID"
                      value={agentId}
                      onChange={(e) => setAgentId(e.target.value)}
                    />
                  )}
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label>Search</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Reason / staff / notes…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Col>

                <Col xs={12} md={6} className="d-flex gap-2 justify-content-md-end">
                  <Button variant="outline-secondary" type="button" onClick={onReset} disabled={loading}>
                    Reset
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    Apply Filters
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Summary chips */}
        <div className="d-flex flex-wrap gap-2 mb-2">
          <Badge bg="dark">Total: {totals.total}</Badge>
          <Badge bg="primary">Holidays: {totals.holidays}</Badge>
          <Badge bg="danger">Off sick: {totals.sick}</Badge>
        </div>

        {/* Table */}
        <Card>
          <Card.Body>
            {loading ? (
              <div className="py-3">
                <Spinner animation="border" size="sm" />{" "}
                <span className="text-muted ms-2">Loading absences…</span>
              </div>
            ) : items.length === 0 ? (
              <div className="small text-muted d-flex align-items-center gap-2">
                <Icon name="info" /> No absences found for selected filters.
              </div>
            ) : (
              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Type</th>
                    <th>Date range</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => {
                    // accept multiple backend naming styles
                    const id = a.id ?? a.absenceId ?? a.staffAbsenceId;
                    const staffName = a.staffName ?? a.name ?? a.agentName ?? `Agent ${a.agentId ?? "-"}`;
                    const t = a.type ?? a.absenceType ?? "-";
                    const st = a.status ?? a.approvalStatus ?? "-";
                    const start = a.startDate ?? a.start ?? a.from ?? a.StartDate;
                    const end = a.endDate ?? a.end ?? a.to ?? a.EndDate;
                    const reason = a.reason ?? a.notes ?? a.Reason ?? "-";

                    const busy = actionBusyId != null && String(actionBusyId) === String(id);

                    // Show buttons like "staff section":
                    // - Approve only if pending
                    // - Cancel unless already cancelled
                    const showApprove = id && isPending(st);
                    const showCancel = id && !isCancelled(st);

                    return (
                      <tr key={id ?? `${a.agentId}-${start}-${end}`}>
                        <td>
                          <div className="fw-semibold">{staffName}</div>
                          <div className="small text-muted">ID: {a.agentId ?? a.staffId ?? "-"}</div>
                        </td>

                        <td>{typeBadge(t)}</td>

                        <td className="small">
                          <div className="fw-semibold">
                            {ymd(start)} → {ymd(end)}
                          </div>
                          <div className="text-muted">{a.isAllDay ?? a.IsAllDay ? "All day" : "Partial"}</div>
                        </td>

                        <td>{statusBadge(st)}</td>

                        <td className="small">{String(reason)}</td>

                        <td className="text-end">
                          <div className="d-inline-flex gap-2">
                            {showApprove ? (
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => approveAbsence(id)}
                                disabled={busy}
                              >
                                {busy ? "..." : "Approve"}
                              </Button>
                            ) : null}

                            {showCancel ? (
                              <Button
                                size="sm"
                                variant={isApproved(st) ? "outline-danger" : "danger"}
                                onClick={() => openCancelConfirm({ ...a, id })}
                                disabled={busy}
                              >
                                {busy ? "..." : "Cancel"}
                              </Button>
                            ) : null}

                            {!showApprove && !showCancel ? (
                              <span className="small text-muted">—</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Cancel confirmation */}
        <Modal show={confirmCancelOpen} onHide={closeCancelConfirm} centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Cancel absence</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2">
              Are you sure you want to cancel this absence?
            </div>

            {cancelTarget ? (
              <div className="small text-muted">
                <div>
                  <span className="fw-semibold">Staff:</span>{" "}
                  {cancelTarget.staffName ?? cancelTarget.name ?? cancelTarget.agentName ?? `Agent ${cancelTarget.agentId ?? "-"}`}
                </div>
                <div>
                  <span className="fw-semibold">Dates:</span>{" "}
                  {ymd(cancelTarget.startDate ?? cancelTarget.start ?? cancelTarget.from ?? cancelTarget.StartDate)} →{" "}
                  {ymd(cancelTarget.endDate ?? cancelTarget.end ?? cancelTarget.to ?? cancelTarget.EndDate)}
                </div>
                <div>
                  <span className="fw-semibold">Type:</span> {String(cancelTarget.type ?? cancelTarget.absenceType ?? "-")}
                </div>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeCancelConfirm} disabled={actionBusyId != null}>
              Close
            </Button>
            <Button variant="danger" onClick={cancelAbsence} disabled={actionBusyId != null}>
              Cancel absence
            </Button>
          </Modal.Footer>
        </Modal>
      </Block>
    </Layout>
  );
}
